/* ++++++++++ --------------- IMPORTS --------------- ++++++++++ */
// libraries
import React, { useState } from 'react'
// utils
import {
  formatXAxisDates,
  getProducts,
  reorderProductsData,
  formatNumber,
  formatProductAnalyticsReportDates,
  sortMetricData
} from '../../utils/charts'
// material
import { ListTable } from '@insticator/insticator-ui'
// styles
import injectSheet from 'react-jss'
import styles from './detailstable_styles'
// static
import { MONETIZATION_TYPE_BETA } from '../../pages/dashboard/monetization/consts'
import ExportDataInCsv from '../exportdata/exportdataincsv'

/* ========== ~~~~~~~~~~ DETAILS TABLE  ~~~~~~~~~~ ========== */
const DetailsTable = injectSheet(styles)((props) => {
  /**
     * Props Definition
     * tableType - used for formatting the data - (monetization, commenting, trivia)
     * period - currently selected rollup (daily, weekly, monthly, quarterly)
     * data - API response data object
     * csvFileName - filename of the exported csv
     * adsType - optional - for monetization only - (TOTAL, DISPLAY, VIDEO)
     * dataType - optional - for monetization, commenting and trivia - check if data should render revenue, rpm, unitload
     */

  
  const formatTableColumns = () => {
    if (!props.data) return null
    const cols = {}
    switch (props.tableType) {
      case MONETIZATION_TYPE_BETA:
        cols.product = 'Product'
        const totalData = props.data[props.period] && props.data[props.period].TOTAL
        if (totalData) {
          Object.keys(totalData).forEach(date => {
            cols[date] = formatXAxisDates(date, props.period)
          })
        }
        break
      case 'monetization':
        cols.product = 'Product'
        const totalDataPerPeriod = props.data.TOTAL && props.data.TOTAL[props.period] && props.data.TOTAL[props.period].TOTAL
        if (totalDataPerPeriod) {
          Object.keys(totalDataPerPeriod).forEach(date => {
            cols[date] = formatXAxisDates(date, props.period)
          })
        }
        break
      case 'commenting':
        // TODO: format/transform the commenting API response object to table columns.
        cols.product = 'Product'
        Object.keys(props.data).forEach(date => {
          cols[date] = formatProductAnalyticsReportDates(date, props.period)
        })
        break
      case 'trivia':
        cols.product = ''
        Object.keys(props.data).forEach(date => {
          cols[date] = formatProductAnalyticsReportDates(date, props.period)
        })
        break
      case 'trending':
        // TODO: format/transform the commenting API response object to table columns.
        cols.product = ''
        Object.keys(props.data).forEach(date => {
          cols[date] = formatProductAnalyticsReportDates(date, props.period)
        })
        break
      default:
        break
    }
    return cols
  }

  const formatNumberToHoursMins = num => {
    const mins = Math.floor(num / 60)
    const secs = parseInt(num % 60)
    let formattedTime = ''

    if (mins > 60) {
      const hours = Math.floor(mins / 60)
      const min = parseInt(mins % 60)
      const formattedHours = hours > 0 ? `${hours}h ` : ''
      const formattedMins = min > 0 ? `${min < 10 ? `0${min}` : min}m` : ''
      formattedTime = `${formattedHours}${formattedMins}`
    } else {
      const formattedMins = mins > 0 ? `${mins}m ` : ''
      const formattedSecs = secs > 0 ? `${secs < 10 ? `0${secs}` : secs}s` : ''
      formattedTime = `${formattedMins}${formattedSecs}`
    }

    return formattedTime
  }

  const formatDataAsTableRows = (columns) => {
    if (!props.data) return null
    let formattedData = []
    switch (props.tableType) {
      case MONETIZATION_TYPE_BETA:
        const selected = sortMetricData(props.data[props.period], props.data.TOTAL)
        formattedData = Object.keys(selected).map((productType) => {
          const productData = selected[productType]
          const rows = {
            ...columns, // add all columns to each row
            product: (props.dimensionMap || []).find(prod => prod.id === productType).name || productType
          }
          if (productType === 'TOTAL' && props.totalAsAvgLabel) rows.product = props.totalAsAvgLabel

          Object.keys(rows).forEach(date => {
            if (isNaN(rows[date]) && !props.totalAsAvgLabel) {
              if ((props.dimensionMap || []).map(p => p.name).indexOf(rows[date]) < 0 && productType !== rows[date]) rows[date] = 0
            }
          }) // Prepopulate columns that doesn't have values
          Object.keys(productData).forEach(date => {
            rows[date] = formatNumber(productData[date].data, props.metricUnit)
          })

          return rows
        })
        break
      case 'monetization':
        const selectedData = reorderProductsData(props.data[props.adsType][props.period])

        formattedData = Object.keys(selectedData).map((productId) => {
          const productData = selectedData[productId]
          const rows = {
            ...columns, // add all columns to each row
            product: getProducts().filter(prod => prod.id === productId).map(prod => prod.name).pop()
          }
          Object.keys(rows).forEach(date => isNaN(rows[date]) && getProducts().map(p => p.name).indexOf(rows[date]) < 0 ? rows[date] = 0 : '') // Prepopulate columns that doesn't have values
          Object.keys(productData).forEach(date => {
            // rows[date] = formatTableCellData(productData[date][props.dataType]) // TODO: Populate each row's columns with data with formatted numbers - keep this for now
            rows[date] = formatNumber(productData[date][props.dataType], props.dataType !== 'unitLoad' ? 'dollar' : '') // Get the formatted value for exported csv
          })

          return rows
        })
        break
      case 'commenting':
        // TODO: format/transform the commenting API response object to table rows.
        formattedData = props.products.map(product => {
          const rows = {
            product: product.id === 'total'
              ? 'Total Comments'
              : product.name
          }
          const dates = Object.keys(props.data)
          dates.forEach(date => {
            // rows[date] = formatDataLabels(props.data[date][product.id]); // TODO: Comment this for now. Confirm details table format.
            const colValue = props.data[date][product.id]
            if (product.id === 'avgPercentOfVisitors') {
              rows[date] =
                colValue < 1
                  ? `${Number(colValue).toFixed(2)}%`.replace(/^0./, '.')
                  : `${Number(colValue).toFixed(1)}%`;
            } else if (product.id === 'avgTimeOnSiteUptick') {
              rows[date] = formatNumberToHoursMins(colValue);
            } else if (props.dataType === 'percent') {
              rows[date] = formatNumber(colValue, props.dataType, true);
            } else {
              rows[date] = formatNumber(colValue, 'number');
            }
          })
          return rows
        })
        break
      case 'trivia':
        // TODO: format/transform the trivia API response object to table rows.
        formattedData = props.products.map(product => {
          const rows = { product: product.name }
          const dates = Object.keys(props.data)
          dates.forEach(date => {
            if (product.id === 'engagementRate' || props.dataType === 'percent') { rows[date] = formatNumber(props.data[date][product.id], 'percent', true) } else { rows[date] = formatNumber(props.data[date][product.id], 'number') }
          })
          return rows
        })
        break
      case 'trending':
        // TODO: format/transform the trending API response object to table rows.
        formattedData = props.products.map(product => {
          const rows = { product: product.name }
          const dates = Object.keys(props.data)
          dates.forEach(date => {
            if (props.dataType === 'percent') { rows[date] = formatNumber(props.data[date][product.id], 'percent', true) } else { rows[date] = formatNumber(props.data[date][product.id], 'number') }
          })
          return rows
        })
        break
      default:
        break
    }
    return formattedData
  }

  const columns = formatTableColumns()
  const formattedData = formatDataAsTableRows(columns)

  return (
        <div className={`detailstable ${props.classes.detailstable}`}>
            { formattedData && formattedData.length > 0 && Object.keys(formattedData[0]).length > 1 &&
                <ExportDataInCsv
                tableType={props.tableType}
                period={props.period}
                dimensionMap={props.dimensionMap}
                csvFileName={props.csvFileName}
                zAxisId={props.zAxisId}
                metricUnit={props.metricUnit}
                columns={columns}
                formattedData={formattedData}
                selectedDimension={props.selectedDimension}
                currentMetric = {props.currentMetric}/>
            }
            <ListTable
                name={'detailstable'}
                type={'standard'}
                variant={'standard'}
                size={'full'}
                paginationLocation={'bottom'}
                data={formattedData}
                columnHeads={columns}
                rowsPerPage={10}
            />
        </div>
  )
})

/* ++++++++++ --------------- EXPORTS --------------- ++++++++++ */
export default DetailsTable
